import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { TABLET_WIDTH } from '../../constants/dims';
import imgHelp from '../../assets/img/helpBg.png';

const Container = styled.section`
  width: 100%;
  position: relative;
  color: ${props => props.theme.about.text};
  background-color: ${props => props.theme.about.backgroundColor};
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  width: 100%;
  height: 436px;
  background-color: #0e91a1;
  background-image: url(${imgHelp});
  background-repeat: none;
  background-size: cover;
  background-position: center;
  color: white !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  position: relative;

  & > h2 {
    text-align: center;
    text-shadow: 0px 0px 10px black;
  }
`;

const Canvas = styled.div`
  max-width: ${TABLET_WIDTH}px;
  padding: 40px 32px;
  box-sizing: border-box;
`;

const Help = () => {
  useEffect(() => {
    document.title = 'Brainquake: Help';
  }, []);

  return (
    <Container>
      <Header>
        <h2>We're here to help you!</h2>
      </Header>

      <Canvas>
        <h3>What is BrainQuake?</h3>

        <p>
          BrainQuake is a globally recognized leader in innovative and effective
          math learning technology for children 10 and up. Our platform offers
          thousands of creative math solving puzzles that develop
          problem-solving skills, critical thinking skills, number sense,
          algebraic thinking and proportional reasoning, among many other math
          competencies. We are dedicated to revealing children’s true math
          potential by breaking what we call the Symbol BarrierTM, the notion
          that everyone can master mathematics, but math’s symbolic
          representations, especially for children K-8, create a language
          problem that impedes learners from demonstrating their true
          understanding and capacity. We’ve won awards and patents for our work,
          including two U.S. Department of Education Small Business Innovation
          Research Awards.
        </p>

        <p>
          With a BrainQuake subscription, your family may access all of our
          puzzle content, our BrainQuake Score -- a new learning metric that
          seeks to more equitably and fairly assess children’s math performance,
          persistence and creativity -- our coming-soon digital manipulatives
          that support transference of conceptual learning in the puzzles to
          math’s symbols and syntax and other subscriber-only content, including
          opportunities to interact with our co-founder and Chief Scientist, and
          NPR’s “Math Guy”, Dr. Keith Devlin. You can join the BrainQuake
          community{' '}
          <a href="https://apps.apple.com/us/developer/brainquake-inc/id600190331">
            here
          </a>
          .
        </p>

        <p>
          Educators, please note: the current release of the BrainQuake app is
          not optimized for school use (we have not yet included rostering and
          teacher dashboards).
        </p>

        <h3>What type of content is available?</h3>

        <p>
          BrainQuake’s puzzles focus on developing problem-solving skills.
          Through an intense review process, we’ve selected three critical areas
          of mathematics and mathematical thinking to include in our puzzles:
          number sense, proportional reasoning, and algebraic thinking (the last
          one presented in a way that involves spatial reasoning). In upcoming
          releases, we will include innovative “math-forward” versions of each
          puzzle that we’ve designed to explicitly support children’s
          transference of their abstract learning in the puzzles to concrete
          understandings of how that knowledge is represented using math’s
          symbols and syntax.
        </p>

        <p>
          We also provide measures of your child’s math proficiency that we
          believe provide more insight and encouragement than binary math
          assessments that reflect only whether a child obtained the right
          answer or not. Just as important, if not more so, we believe that our
          measures are more equitably derived because they are rooted in an
          interface that explicitly intends to avoid the implicit oppression of
          math’s symbols and syntax for certain subgroups of children.
        </p>

        <h3>Is BrainQuake safe for my children?</h3>
        <p>
          BrainQuake was created by, among others, parents and teachers. We take
          safety and privacy very seriously. In fact, as part of our attempt to
          build more equitable math content, our puzzles do not require us to
          obtain any personally identifiable information in order to deliver a
          powerful math experience. This is because breaking the Symbol Barrier
          allows learners to engage more directly with the math content and
          without, or with less, anxiety and stereotype threat.
        </p>

        <p>
          We do not include links to any other products or advertisements in our
          apps. We do not aggregate and sell or share your children’s data,
          except when required to do so by local or federal law, in which case
          we will comply with the appropriate legal authorities.
        </p>

        <p>
          We use a third-party product, PlayFab, to generate a player
          identification number that you can find in the Settings. This
          identification number is connected to your device ID. Most, but not
          all, states do not consider the device ID to be PII. The only reason
          that BrainQuake gathers the device ID is to support multiple players
          saving their individual games inside the app.
        </p>

        <p>
          All of the content in the BrainQuake app has been created by us. We
          recognize that algorithms, such as those that drive our adaptive
          engines and our BrainQuake Score are, like anything else a human
          produces, subject to reflecting and prioritizing our own implicit
          biases. We seek to overcome these biases by focusing on creating
          interfaces to mathematics that, to the highest degree possible, not
          only remove our own fingerprints, but reflect the best research
          available for how to create learning environments that are safe and
          productive for diverse learners. We recognize that we have not, and
          may never, achieve perfection, but we aspire to do so every day.
        </p>

        <p>
          It is essential that we earn your trust, and we hope to learn that we
          are worthy of it.
        </p>

        <h3>
          I don’t want my children in front of screens all day. How long should
          I let them use BrainQuake?
        </h3>

        <p>
          As parents, we share this concern. We have specifically developed our
          application to be effective in the smallest dosage possible. To date,
          third-party research, that we did not fund, has shown that as little
          as thirty minutes a week can produce measurable improvement (as high
          as 16% in some key components) in performance in the areas specified
          in our declared learning goals after just one month.
        </p>

        <h3>I forgot my password. What should I do?</h3>

        <p>
          For now, the only password necessary to use the BrainQuake app is your
          Apple ID login credentials. BrainQuake does not have access to, and
          thus does not store, any of your passwords. If you forgot or lost your
          Apple ID password, please visit{' '}
          <a href="https://support.apple.com/apple-id">
            https://support.apple.com/apple-id
          </a>
          .
        </p>

        <h3>Does BrainQuake offer a version for Educators?</h3>

        <p>
          As of right now, the BrainQuake app is not optimized for educators. We
          expect to release a version optimized for Educators and schools in the
          near future.
        </p>

        <h3>How can I contact BrainQuake?</h3>

        <p>
          We’d love to hear from you! You can reach us at{' '}
          <a href="mailto:info@brainquake.com">info@brainquake.com</a> for
          general comments or inquiries, or, if you’re having trouble with our
          products, you can reach us at{' '}
          <a href="mailto:support@brainquake.com">support@brainquake.com</a>.
        </p>
      </Canvas>
    </Container>
  );
};

export default Help;
